'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useTranslations } from 'next-intl';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './dialog';
import { Button } from './button';
import { Input } from './input';

export interface ConfirmOptions {
  title: ReactNode;
  body?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: 'default' | 'danger';
  /**
   * Confirm stays disabled until this text is typed: case-sensitive, compared after NFC and
   * collapsing runs of whitespace to one space (HTML collapses them in the label anyway).
   */
  typedConfirm?: string;
  /** Runs with the confirm button in its loading state. A throw keeps the dialog open. */
  onConfirm?: () => void | Promise<void>;
  /**
   * Shown in place of the generic "An error occurred" line when `onConfirm` throws. A function
   * receives the thrown error, so one `onConfirm` can pick the copy per failure. Omit it for the
   * default line.
   */
  errorMessage?: ReactNode | ((error: unknown) => ReactNode);
}

export interface ConfirmDialogProps extends ConfirmOptions {
  open: boolean;
  /** true after a successful confirm; false on cancel, Escape, backdrop or close. */
  onResolve: (confirmed: boolean) => void;
}

/** NFC, runs of whitespace collapsed to one space, trimmed: what the reader sees in the label. */
function normaliseTyped(text: string): string {
  return text.normalize('NFC').replace(/\s+/g, ' ').trim();
}

export function ConfirmDialog({
  open,
  onResolve,
  title,
  body,
  confirmLabel,
  cancelLabel,
  tone = 'default',
  typedConfirm,
  onConfirm,
  errorMessage,
}: ConfirmDialogProps) {
  const t = useTranslations('Common');
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [failure, setFailure] = useState<unknown>(undefined);
  const inFlight = useRef(false);
  // A blank expected value means there is no typed step at all.
  const expected = typedConfirm === undefined ? undefined : normaliseTyped(typedConfirm);
  const typedOk = !expected || normaliseTyped(typed) === expected;

  useEffect(() => {
    if (!open) {
      setTyped('');
      setFailed(false);
    }
  }, [open]);

  async function handleConfirm() {
    if (!typedOk || inFlight.current) return;
    if (!onConfirm) {
      onResolve(true);
      return;
    }
    inFlight.current = true;
    setBusy(true);
    setFailed(false);
    try {
      await onConfirm();
      inFlight.current = false;
      setBusy(false);
      onResolve(true);
    } catch (error) {
      console.error('[ConfirmDialog] onConfirm failed', error);
      inFlight.current = false;
      setBusy(false);
      setFailure(error);
      setFailed(true);
    }
  }

  const focusCancel = tone === 'danger' && !expected;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onResolve(false);
      }}
      size="sm"
      busy={busy}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            void handleConfirm();
          }}
        >
          {body || expected || failed ? (
            <div className="text-foreground space-y-3 p-6 text-sm">
              {body ? <div>{body}</div> : null}
              {expected ? (
                <label className="block space-y-1.5">
                  <span className="text-muted text-xs">
                    {t('typeToConfirm', { word: expected })}
                  </span>
                  <Input
                    value={typed}
                    onChange={(event) => setTyped(event.target.value)}
                    autoComplete="off"
                    spellCheck={false}
                    data-autofocus=""
                  />
                </label>
              ) : null}
              {failed ? (
                <p role="alert" className="text-danger text-sm">
                  {errorMessage === undefined
                    ? t('ui.error')
                    : typeof errorMessage === 'function'
                      ? errorMessage(failure)
                      : errorMessage}
                </p>
              ) : null}
            </div>
          ) : null}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={busy}
              onClick={() => onResolve(false)}
              {...(focusCancel ? { 'data-autofocus': '' } : {})}
            >
              {cancelLabel ?? t('buttons.cancel')}
            </Button>
            <Button
              type="submit"
              variant={tone === 'danger' ? 'destructive' : 'primary'}
              loading={busy}
              disabled={!typedOk}
              {...(!focusCancel && !expected ? { 'data-autofocus': '' } : {})}
            >
              {confirmLabel ?? t('buttons.confirm')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

interface PendingConfirm {
  id: number;
  options: ConfirmOptions;
  resolve: (confirmed: boolean) => void;
}

const ConfirmContext = createContext<ConfirmFn | undefined>(undefined);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [queue, setQueue] = useState<PendingConfirm[]>([]);
  const nextId = useRef(0);

  const confirm = useCallback<ConfirmFn>(
    (options) =>
      new Promise<boolean>((resolve) => {
        nextId.current += 1;
        const id = nextId.current;
        setQueue((current) => [...current, { id, options, resolve }]);
      }),
    [],
  );

  const head = queue[0];

  function resolveHead(confirmed: boolean) {
    if (!head) return;
    head.resolve(confirmed);
    setQueue((current) => current.filter((entry) => entry.id !== head.id));
  }

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {head ? <ConfirmDialog key={head.id} open {...head.options} onResolve={resolveHead} /> : null}
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error('useConfirm must be used within a ConfirmProvider');
  }
  return context;
}
